import React, { useState } from 'react';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { translateI18nError } from './i18nError';

interface ConfirmOptions {
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

/**
 * Casilla dentro del propio diálogo de confirmación.
 *
 * Nace desmarcada a propósito: la acción segura es no propagar. Quien quiera que el
 * borrado llegue a Siigo tiene que decirlo, y lo dice aquí, en el momento de borrar.
 */
export interface OpcionConfirmacion {
  etiqueta: string;
  descripcion?: string;
  /** by default `false`. Marcarla by default convertiría un descuido en una escritura. */
  marcadaPorDefecto?: boolean;
}

export interface ResultadoConfirmacion {
  confirmado: boolean;
  /** `true` solo si la casilla existía y quedó marcada. */
  opcionMarcada: boolean;
}

interface PromptOptions extends ConfirmOptions {
  placeholder?: string;
  defaultValue?: string;
  /** Si es true, no deja confirmar con the field vacío (ej. un motivo obligatorio). */
  required?: boolean;
}

/**
 * Cuerpo de la confirmación con casilla. Componente propio por la misma razón que
 * `PromptBody`: necesita `useState`, y un hook no puede vivir en la función que `toast()`
 * invoca para renderizar.
 */
const ConfirmacionConOpcion: React.FC<{
  message: string;
  opcion: OpcionConfirmacion;
  options?: ConfirmOptions;
  onResolver: (resultado: ResultadoConfirmacion) => void;
}> = ({ message, opcion, options, onResolver }) => {
  const [marcada, setMarcada] = useState(!!opcion.marcadaPorDefecto);
  return (
    <div className="flex min-w-[260px] flex-col gap-3">
      <p className="whitespace-pre-line text-sm font-bold text-slate-700 dark:text-slate-200">{message}</p>

      <label className="flex cursor-pointer items-start gap-2 rounded-lg bg-slate-50 p-2.5 dark:bg-slate-800/60">
        <input
          type="checkbox"
          checked={marcada}
          onChange={(e) => setMarcada(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-rose-600"
        />
        <span className="min-w-0">
          <span className="block text-xs font-bold text-slate-700 dark:text-slate-200">{opcion.etiqueta}</span>
          {opcion.descripcion && (
            <span className="mt-0.5 block text-[0.6875rem] leading-relaxed text-slate-500 dark:text-slate-400">
              {opcion.descripcion}
            </span>
          )}
        </span>
      </label>

      <div className="flex justify-end gap-2">
        <button
          onClick={() => onResolver({ confirmado: false, opcionMarcada: false })}
          className="rounded-lg px-3 py-1.5 text-xs font-bold text-slate-500 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          {options?.cancelLabel ?? i18n.t('common.cancel')}
        </button>
        <button
          onClick={() => onResolver({ confirmado: true, opcionMarcada: marcada })}
          className={`rounded-lg px-3 py-1.5 text-xs font-bold text-white transition-colors ${
            options?.danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-brand-600 hover:bg-blue-800'
          }`}
        >
          {options?.confirmLabel ?? i18n.t('common.confirm')}
        </button>
      </div>
    </div>
  );
};

/** Cuerpo del prompt. Vive como componente propio (no como JSX inline dentro del toast) porque
 * necesita estado local para the field de texto -- un `useState` no puede vivir en la función
 * render que toast() invoca. */
const PromptBody: React.FC<{
  message: string;
  options?: PromptOptions;
  onCancel: () => void;
  onSubmit: (valor: string) => void;
}> = ({ message, options, onCancel, onSubmit }) => {
  const [valor, setValor] = useState(options?.defaultValue ?? '');
  const bloqueado = !!options?.required && !valor.trim();
  return (
    <form
      className="flex flex-col gap-3 min-w-65"
      onSubmit={(e) => { e.preventDefault(); if (!bloqueado) onSubmit(valor); }}
    >
      <p className="text-sm font-bold text-slate-700 dark:text-slate-200 whitespace-pre-line">{message}</p>
      <input
        autoFocus
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        placeholder={options?.placeholder}
        className="w-full border border-slate-200 dark:border-slate-700 px-3 py-2 rounded-lg text-xs font-medium bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 outline-none focus:border-brand-600"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          {options?.cancelLabel ?? i18n.t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={bloqueado}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-colors disabled:opacity-40 ${
            options?.danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-brand-600 hover:bg-blue-800'
          }`}
        >
          {options?.confirmLabel ?? i18n.t('common.confirm')}
        </button>
      </div>
    </form>
  );
};

/**
 * Capa única de notificaciones para toda la app. Reemplaza alert()/confirm()
 * nativos por toasts consistentes (react-hot-toast), incluyendo un
 * confirm() asíncrono no bloqueante con botones de acción.
 */
export const notify = {
  success: (message: string) => toast.success(message),
  error: (message: string) => toast.error(translateI18nError(message, (k, o) => String(i18n.t(k, o)))),
  // `description`, if provided, renders on a dimmer second line under the title -- used by the
  // real-time notification toast (event title + its real message, see NotificationBell.tsx),
  // instead of a flat single-line toast.
  info: (message: string, description?: string) => toast(
    description
      ? (
        <div className="flex flex-col gap-0.5">
          <p className="font-bold text-sm leading-snug">{message}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug line-clamp-2">{description}</p>
        </div>
      )
      : message,
    { icon: 'ℹ️' }
  ),

  /**
   * Confirmar y, en el mismo paso, decidir algo más sobre la acción.
   *
   * is used donde borrar aquí *puede* tener consecuencia fuera: una operación con factura
   * en Siigo, un recibo de caja ya enviado, un cliente ya creado allá. La casilla solo se
   * ofrece cuando esa fila concreta tiene contraparte; ofrecerla siempre sugeriría que
   * borrar una cotización toca la contabilidad.
   */
  confirmConOpcion: (
    message: string,
    opcion: OpcionConfirmacion,
    options?: ConfirmOptions,
  ): Promise<ResultadoConfirmacion> => {
    return new Promise((resolve) => {
      toast((t) => (
        <ConfirmacionConOpcion
          message={message}
          opcion={opcion}
          options={options}
          onResolver={(resultado) => { toast.dismiss(t.id); resolve(resultado); }}
        />
      ), { duration: 30000 });
    });
  },

  confirm: (message: string, options?: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      toast((t) => (
        <div className="flex flex-col gap-3 min-w-[220px]">
          <p className="text-sm font-bold text-slate-700 dark:text-slate-200 whitespace-pre-line">{message}</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(false);
              }}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              {options?.cancelLabel ?? i18n.t('common.cancel')}
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(true);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-colors ${
                options?.danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-brand-600 hover:bg-blue-800'
              }`}
            >
              {options?.confirmLabel ?? i18n.t('common.confirm')}
            </button>
          </div>
        </div>
      ), { duration: 20000 });
    });
  },

  /**
   * Confirm con un campo de texto. Resuelve al valor escrito, o a `null` si se canceló --
   * distinguir "canceló" de "confirmó dejándolo vacío" importa: cancelar una recolección sin
   * motivo es válido, no programarla no lo es.
   */
  prompt: (message: string, options?: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      toast((t) => (
        <PromptBody
          message={message}
          options={options}
          onCancel={() => { toast.dismiss(t.id); resolve(null); }}
          onSubmit={(valor) => { toast.dismiss(t.id); resolve(valor); }}
        />
      ), { duration: 60000 });
    });
  },
};
